import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Clock, MessageCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatARS } from "@/lib/format";
import { waLink, generalMsg } from "@/lib/whatsapp";

const STATUS_MAP = {
  approved: { icon: CheckCircle2, cls: "text-volt", title: "¡Pago aprobado!", desc: "Gracias por tu compra. Coordinamos la entrega por WhatsApp." },
  pending: { icon: Clock, cls: "text-yellow-400", title: "Pago pendiente", desc: "Tu pago está en proceso. Te avisamos cuando se acredite." },
  in_process: { icon: Clock, cls: "text-yellow-400", title: "Pago en proceso", desc: "Tu pago está en proceso. Te avisamos cuando se acredite." },
  rejected: { icon: AlertCircle, cls: "text-red-400", title: "Pago rechazado", desc: "El pago fue rechazado. Probá con otro medio de pago o escribinos por WhatsApp." },
  cancelled: { icon: AlertCircle, cls: "text-red-400", title: "Pago cancelado", desc: "El pago fue cancelado. Podés intentarlo de nuevo cuando quieras." },
  pedido_whatsapp: { icon: CheckCircle2, cls: "text-volt", title: "¡Pedido registrado!", desc: "Recibimos tu pedido. Coordinamos el pago y la entrega por WhatsApp." },
  error: { icon: AlertCircle, cls: "text-red-400", title: "Error en el pedido", desc: "Hubo un problema al procesar tu pedido. Escribinos por WhatsApp." },
};

export default function CheckoutResult() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [order, setOrder] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      return undefined;
    }
    let tries = 0;
    let timer;
    const fetchOrder = async () => {
      try {
        const { data } = await api.get(`/pedidos/${orderId}`);
        setOrder(data);
        const open = ["creada", "pending", "in_process"].includes(data.status);
        if (open && tries < 15) {
          tries += 1;
          timer = setTimeout(fetchOrder, 4000);
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail));
        setOrder(null);
      }
    };
    fetchOrder();
    return () => clearTimeout(timer);
  }, [orderId]);

  const status = order ? STATUS_MAP[order.status] || STATUS_MAP.pedido_whatsapp : order === null ? null : undefined;
  const Icon = status?.icon;

  return (
    <main className="mx-auto max-w-2xl px-5 pb-24 pt-36 sm:px-8 lg:pt-44">
      {status ? (
        <div className="border border-white/10 bg-panel p-8 text-center sm:p-12" data-testid="checkout-result-panel">
          <Icon className={`mx-auto h-12 w-12 ${status.cls}`} />
          <h1 className="mt-5 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl" data-testid="checkout-result-title">
            {status.title}
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-zinc-400">{status.desc}</p>
          <p className="mt-5 font-mono text-sm text-zinc-500">
            Pedido: <span className="font-bold text-volt">{order.order_id}</span>
          </p>
          <div className="mt-8 space-y-2 border-t border-white/10 pt-6 text-left">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-zinc-400">{i.qty}x {i.name}{i.size ? ` · ${i.size}` : ""}</span>
                <span className="font-mono text-zinc-300">{formatARS(i.unit_price * i.qty)}</span>
              </div>
            ))}
            {order.discount > 0 && (
              <div className="flex justify-between font-mono text-sm font-bold text-volt">
                <span>10% OFF 2da prenda</span>
                <span>-{formatARS(order.discount)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-white/10 pt-3 font-display text-xl font-black uppercase text-white">
              <span>Total</span>
              <span className="font-mono" data-testid="checkout-result-total">{formatARS(order.total)}</span>
            </div>
          </div>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a href={waLink(generalMsg())} target="_blank" rel="noreferrer" data-testid="checkout-result-whatsapp-button" className="inline-flex items-center justify-center gap-2 bg-wa px-6 py-3.5 font-display text-sm font-black uppercase tracking-widest text-black transition-colors hover:bg-[#20bd5a]">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <Link to="/tienda" data-testid="checkout-result-back-button" className="inline-flex items-center justify-center border border-white/20 px-6 py-3.5 font-display text-sm font-black uppercase tracking-widest text-white transition-colors hover:border-volt hover:text-volt">
              Seguir comprando
            </Link>
          </div>
        </div>
      ) : order === null ? (
        <div className="border border-white/10 bg-panel p-10 text-center" data-testid="checkout-result-not-found">
          <h1 className="font-display text-2xl font-black uppercase text-white">
            {error || "Pedido no encontrado"}
          </h1>
          <p className="mt-3 text-sm text-zinc-400">Revisá el enlace o escribinos por WhatsApp y lo resolvemos al toque.</p>
          <a href={waLink(generalMsg())} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 bg-wa px-6 py-3 font-display text-sm font-black uppercase tracking-widest text-black">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
        </div>
      ) : (
        <p className="pt-20 text-center font-mono text-sm uppercase tracking-widest text-zinc-500">Cargando estado del pago...</p>
      )}
    </main>
  );
}
